import apiClient from '../config/axios';

export interface BranchManager {
  id: number;
  manager_id: string;
  branch?: number;
  branch_id?: number;
  username: string;
  manager_name: string;
  email: string;
  phone: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  // Additional fields that might be available
  first_name?: string;
  last_name?: string;
  position?: string;
  department?: string;
  hire_date?: string;
  salary?: number;
  emergency_contact?: string;
  emergency_phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  alternate_email?: string;
  alternate_phone?: string;
  notes?: string;
  permissions?: string[];
  last_login?: string;
}

export interface BranchManagerRegistrationData {
  branch_id: number;
  username: string;
  password?: string;
  manager_name: string;
  email: string;
  phone: string;
  is_active?: boolean;
}

export interface BranchManagerLoginData {
  email: string;
  // username: string; // commented out for email-only login
  // password: string; // commented out for email-only login
}

export interface BranchManagerResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: {
      id: number;
      username: string;
      email: string;
      manager_name: string;
      branch_id: number;
      is_main_seller: boolean;
    };
  };
}

export interface BranchManagerDetailResponse {
  status: string;
  data: {
    id: number;
    attributes: BranchManager;
  };
}

export interface BranchManagerRegistrationResponse {
  status: string;
  message: string;
  data: {
    id: number;
    attributes: BranchManager;
  };
  token: string;
  expiresIn: string;
}

export interface BranchManagersListResponse {
  status: string;
  data: Array<{
    id: number;
    attributes: BranchManager;
  }>;
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      pageCount: number;
    };
  };
}

class BranchManagerService {
  /**
   * Register a new branch manager
   */
  async registerBranchManager(managerData: BranchManagerRegistrationData): Promise<BranchManagerRegistrationResponse> {
    const response = await apiClient.post('/branches/managers', managerData);
    return response.data;
  }

  /**
   * Login a branch manager
   */
  async loginBranchManager(loginData: BranchManagerLoginData): Promise<BranchManagerResponse> {
    const response = await apiClient.post('/branches/managers/login', loginData);
    return response.data;
  }

  /**
   * Get all branch managers with optional filtering
   */
  async getBranchManagers(params?: {
    is_active?: boolean;
    search?: string;
    branch_id?: number;
    page?: number;
    pageSize?: number;
  }): Promise<BranchManagersListResponse> {
    const response = await apiClient.get('/branches/managers', { params });
    return response.data;
  }

  /**
   * Get a specific branch manager by ID
   */
  async getBranchManagerById(managerId: number): Promise<BranchManagerDetailResponse> {
    const response = await apiClient.get(`/branches/managers/${managerId}`);
    return response.data;
  }

  /**
   * Update a branch manager
   */
  async updateBranchManager(managerId: number, updateData: Partial<BranchManager>): Promise<BranchManagerResponse> {
    const response = await apiClient.put(`/branches/managers/${managerId}`, updateData);
    return response.data;
  }

  /**
   * Delete a branch manager (hard delete)
   */
  async deleteBranchManager(managerId: number): Promise<BranchManagerResponse> {
    const response = await apiClient.delete(`/branches/managers/${managerId}`);
    return response.data;
  }

  /**
   * Activate a branch manager
   */
  async activateBranchManager(managerId: number): Promise<BranchManagerResponse> {
    const response = await apiClient.patch(`/branches/managers/${managerId}/activate`);
    return response.data;
  }

  /**
   * Deactivate a branch manager (soft delete)
   */
  async deactivateBranchManager(managerId: number): Promise<BranchManagerResponse> {
    const response = await apiClient.patch(`/branches/managers/${managerId}/deactivate`);
    return response.data;
  }

  /**
   * Get managers by branch ID
   */
  async getManagersByBranch(branchId: number, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchManagersListResponse> {
    const response = await apiClient.get(`/branches/managers/branch/${branchId}`, { params });
    return response.data;
  }

  /**
   * Search branch managers
   */
  async searchBranchManagers(searchTerm: string, params?: {
    page?: number;
    pageSize?: number;
  }): Promise<BranchManagersListResponse> {
    const response = await apiClient.get(`/branches/managers/search/${searchTerm}`, { params });
    return response.data;
  }
}

const branchManagerService = new BranchManagerService();
export default branchManagerService;
